import React, { useCallback, useMemo, useState } from 'react'
import { View, StyleSheet, TouchableOpacity, Platform, ScrollView } from 'react-native'
import { Text, useTheme } from '@ui-kitten/components'
import ProductSearch from './ProductSearch'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { currencyFormatterAtom, productCategoriesDictAtom, productUnitsDictAtom, productsDictAtom, userRoleAtom } from '../global/AtomsProvider'
import { getProductsForNewSaleByCategoryList } from '../utils/productListHelpers'
import { FlashList } from '@shopify/flash-list'
import { roundNumber } from '../utils/numberUtils'


const PRODUCT_CARD_WIDTH = 100
const PRODUCT_CARD_HEIGHT = 120
const PRODUCT_CARD_MARGIN = 5
const PRODUCT_CARD_PADDING = 5
const PRODUCT_CARD_NAME_LINES = 7

const SingleCategorySelectView = React.memo(function SingleCategorySelectView({onSelectCategory, categoryId, categoryIndex, categoryTitle}) {
  const theme = useTheme()

  const onPressAction = (e) => {
    e.target.blur()
    if (e.keyCode === 13) {
      return
    }
    // Report WHAT was tapped, not just where it sat: the parent stores the title so the
    // selection survives a category being dropped from the derived list (ATL-1366).
    onSelectCategory(categoryIndex, categoryId)
  }

  return (
    <TouchableOpacity style={[styles.categoryButton, { borderColor: theme['color-primary-500'] }]} onPress={onPressAction}>
      <Text style={[styles.categoryButtonText, { color: theme['color-primary-500'] }]}>{categoryTitle}</Text>
    </TouchableOpacity>
  )
}, function (prev, next) {
  // ATL-1366: this compared `prev.store`, a prop this component is never given — so both sides
  // were always `undefined`, the comparator always said "equal", and a mounted tab NEVER
  // re-rendered. That was invisible while the category list was built once into state; now that
  // it is derived, hiding the last product of a category drops that category and every tab after
  // it shifts down one, keeping its frozen label: the button still reads «Десерты» and selects
  // «Напитки». Compare what the tab actually renders and acts on.
  if (prev.categoryId !== next.categoryId) return false
  if (prev.categoryIndex !== next.categoryIndex) return false
  if (prev.categoryTitle !== next.categoryTitle) return false
  if (prev.onSelectCategory !== next.onSelectCategory) return false
  return true
})

const SingleCategoryDetailView = React.memo(function SingleCategoryDetailView({category, addNewCartItem, store, defaultProductUnit}) {
  
  const [columns, setColumns] = useState(3)


  const onLayout = (event) => {
    const { width } = event.nativeEvent.layout;
    setColumns(Math.floor(width / (PRODUCT_CARD_WIDTH)))
  }

  if (!category) {
    return null
  }
  return (
    <View onLayout={onLayout} style={styles.categoryContainer} key={category.id}>
      <Text category='label' style={styles.categoryName}>
        {category.title}
      </Text>
      <FlashList
        data={category.data}
        renderItem={({item, index}) => (
          <ProductGridCard key={item.id} product={item} addNewCartItem={addNewCartItem} store={store} defaultProductUnit={defaultProductUnit} />
        )}
        numColumns={columns}
        estimatedItemSize={PRODUCT_CARD_HEIGHT}
        ListFooterComponent={() => <View style={{height: 100}}></View>}
      />
    </View>
  )
}, function (prev, next) {
  // ATL-1366: this used to accept a category as unchanged whenever its title and its ROW COUNT
  // matched, which let every same-length catalogue change stop here — the grid kept rendering the
  // previous product objects and `addNewCartItem` was handed one of them, so an edited price was
  // neither shown nor charged, and a snapshot that hid one product while activating another in the
  // same category left the hidden one on the grid and sellable. `getProductsForNewSaleByCategoryList`
  // builds a fresh category object per recompute, so an identity check is exactly "the catalogue
  // changed" — and it also removes the unguarded `prev.category.data` dereference.
  if (prev.store !== next.store) return false
  if (prev.category !== next.category) return false
  if (prev.addNewCartItem !== next.addNewCartItem) return false
  if (prev.defaultProductUnit !== next.defaultProductUnit) return false
  return true
})

const ProductGridCard = React.memo(function ProductGridCard({addNewCartItem, product, store, defaultProductUnit}) {
  const theme = useTheme()
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  
  const onPressAction = (e) => {
    e.target.blur()
    if (e.keyCode === 13) {
      if (Platform.OS === 'web') {
        document.dispatchEvent(new KeyboardEvent('keydown', {'keyCode': 13, 'which': 13}))
      }
      return
    }
    addNewCartItem(product)
  }

  return (
    <TouchableOpacity onPress={onPressAction} testID={`product-card-${product.id}`}>
      <View style={[styles.productContainer, { backgroundColor: theme['background-basic-color-1'] }]}>
        <Text style={styles.productName} numberOfLines={PRODUCT_CARD_NAME_LINES}>
          {product.name}
        </Text>
        { product.id !== 'customProduct' && (
          <Text style={styles.productPrice}>
            {currencyFormatter?.format(product.salePrices?.[store])}
          </Text>
        )}
        { !(product.isService || (product.isComposite && !product.manufacturedBeforeSale) || product.id === 'customProduct') && (
          <Text style={styles.productPrice}>
            {'(' + roundNumber(product.storeStock?.[store] || 0) + ' ' + (productUnitsDict[product.productUnit]?.shortName || defaultProductUnit) + ')'}
          </Text>
        )}
      </View>
    </TouchableOpacity>
  )
}, function(prev, next) {
  if (prev.store !== next.store) return false
  if (prev.product?.id !== next.product?.id) return false
  if (prev.addNewCartItem !== next.addNewCartItem) return false
  if (prev.defaultProductUnit !== next.defaultProductUnit) return false
  if (prev.product?.salePrices?.[prev.store] !== next.product?.salePrices?.[next.store]) return false
  if (prev.product?.storeStock?.[prev.store] !== next.product?.storeStock?.[next.store]) return false
  // ATL-1366: compare everything the card RENDERS, not just price and stock. Now that the
  // catalogue is derived, the category above re-renders on every update and this comparator is
  // the last thing standing between a product edit and the tile — so a rename, a unit change, or
  // a flip of isService / isComposite / manufacturedBeforeSale (which decides whether the stock
  // line is drawn at all) used to be swallowed here and the old tile stayed on screen until the
  // cashier switched category. Deliberately field-by-field rather than an identity check: the
  // products dict is REPLACED wholesale on every realtime stock event, so `prev.product !==
  // next.product` would re-render every tile in the grid on every event.
  if (prev.product?.name !== next.product?.name) return false
  if (prev.product?.productUnit !== next.product?.productUnit) return false
  if (prev.product?.isService !== next.product?.isService) return false
  if (prev.product?.isComposite !== next.product?.isComposite) return false
  if (prev.product?.manufacturedBeforeSale !== next.product?.manufacturedBeforeSale) return false
  return true
})

export const ProductGrid = React.memo(function ProductGrid(props) {
  const { t } = useTranslation()
  // ATL-1366: remember WHICH category the cashier tapped, not only where it sat. See the
  // resolution below the memo for why a bare index is no longer safe.
  const [selectedCategory, setSelectedCategory] = useState({index: 0, id: null})
  const onSelectCategory = useCallback((index, id) => setSelectedCategory({index, id}), [])
  const productsDict = useAtomValue(productsDictAtom)
  const productCategories = useAtomValue(productCategoriesDictAtom)
  const userRole = useAtomValue(userRoleAtom)

  // ATL-1366: this list used to be built ONCE into state, behind an effect keyed on
  // `Object.keys(productsDict).length` — the product COUNT. Editing a product never changes the
  // count, so hiding one (isHiddenInNewSale) left it on the grid, at its price, for the rest of
  // the session; only a restart cleared it. The catalogue has to be derived from the dict the
  // Firestore/Supabase subscription replaces, which is what `useMemo` does. Same memo, same deps
  // and same cost as AddCartItemScreen's — the sort is O(n log n) and runs per catalogue change,
  // not per render (ATL-968).
  const productsForNewSaleByCategoryList = useMemo(
    () => getProductsForNewSaleByCategoryList(productsDict, productCategories, (userRole?.permissions?.['sellCustomProducts'] || userRole?.id === 'admin'), true, props.store),
    [productsDict, productCategories, userRole, props.store]
  )
  if (productsForNewSaleByCategoryList.length === 0) {
    return null
  }
  // A recomputed catalogue can be SHORTER than the one the merchant last tapped a tab on (hiding
  // the last product of a category drops the category outright), so a stored index can point past
  // the end — and, worse, dropping an EARLIER category shifts every later tab down one, so the
  // same index silently selects a different category. That is the selection half of the defect the
  // tab-label comparator fixes above: the button would read «Десерты» and the grid would show
  // «Напитки» under a cashier's finger mid-order. Resolve the remembered category ID back to its
  // current position (names are not unique), and fall back to the clamped index only when that
  // category is genuinely gone.
  const categoryIndex = selectedCategory.id === null
    ? -1
    : productsForNewSaleByCategoryList.findIndex((category) => category.id === selectedCategory.id)
  const safeIndex = categoryIndex >= 0
    ? categoryIndex
    : Math.min(selectedCategory.index, productsForNewSaleByCategoryList.length - 1)
  
  return (
    <View style={styles.rightContainer}>
      <ScrollView style={{flex: 1, padding: 20}}>
        <ProductSearch onSelectCallback={props.addNewCartItem} store={props.store} excludeHiddenInNewSale={true} />
        <View style={styles.categoryButtonGroup}>
          { productsForNewSaleByCategoryList?.map((category, index) => (
            <SingleCategorySelectView key={category.id} onSelectCategory={onSelectCategory} categoryId={category.id} categoryIndex={index} categoryTitle={category.title} />
          )) }
        </View>
        <SingleCategoryDetailView key={safeIndex.toString()} category={productsForNewSaleByCategoryList[safeIndex]} addNewCartItem={props.addNewCartItem} store={props.store} defaultProductUnit={t('pcs')} />
      </ScrollView>
    </View>
  )
}, function(prev, next) {
  if (prev.store !== next.store) return false
  if (prev.addNewCartItem !== next.addNewCartItem) return false
  return true
})


const styles = StyleSheet.create({
  rightContainer: {
    flex: 1
  },
  productContainer: {
    // marginRight: PRODUCT_CARD_MARGIN,
    marginVertical: PRODUCT_CARD_MARGIN,
    padding: PRODUCT_CARD_PADDING,
    height: PRODUCT_CARD_HEIGHT,
    width: PRODUCT_CARD_WIDTH,
    borderRadius: 5,
  },
  productName: {
    fontSize: 12,
    lineHeight: 12,
    marginBottom: 5,
    fontWeight: 'bold',
  },
  productPrice: {
    fontSize: 12,
    lineHeight: 14,
  },
  categoryButtonGroup: {
    marginTop: 15,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  categoryButton: {
    marginRight: 10,
    marginBottom: 10,
    padding: 10,
    borderWidth: 1,
    borderRadius: 5,
  },
  categoryButtonText: {
  },
  categoryContainer: {

  }
})
